/**
 * Змагання e-phan у формі заходу сайту.
 *
 * Сайт має одну форму запису для календаря, звідки б захід не прийшов, — інакше
 * кожна картка мусила б знати про два джерела. Тому змагання перекладається
 * тут, один раз, а не в компонентах.
 *
 * Чого в джерелі немає, лишається порожнім: партнери, організації-співзасновники,
 * ознака спартаківського заходу, посилання на суддівську колегію й карта
 * готелів. Вигадувати їх не можна — це офіційний календар. Усе це редактор
 * додає доповненням у CMS (див. mergeEvents).
 *
 * Календар більше не відсіює закордонні змагання (ADR-0012 фільтром
 * `country=UA` на джерелі, який лишав календар порожнім): e-phan веде не
 * лише українські старти, і відвідувачу вони теж цікаві. Замість відсіву —
 * позначка `foreign` на картці, див. `isUkrainian`.
 */

/** Код типу e-phan, який для нас означає рейтинговий захід. */
const RANKING = 'ranking_tournament';

/** Місце проведення одним рядком. */
function venueOf(location = {}) {
  return [location.venueName, location.venueAddress]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(', ') || null;
}

/**
 * Чи справді запис український.
 *
 * `country=UA` застосовує сервер джерела, але покладатися лише на нього не
 * варто: `countryCode` у схемі оголошений nullable, а `country` приходить
 * рядком, і за назвою країни сервер теж уміє шукати.
 */
export function isUkrainian(competition) {
  const code = competition?.location?.countryCode;
  if (code) return String(code).toUpperCase() === 'UA';

  const name = String(competition?.location?.country || '')
    .trim()
    .toLowerCase();

  return name === 'ukraine' || name === 'україна';
}

export function eventFromCompetition(competition) {
  const { location = {}, dates = {}, registration = {}, links = {}, media = {}, banner, type } = competition;

  const start = dates.start || null;
  const end = dates.end && dates.end !== start ? dates.end : null;

  return {
    id: competition.slug,
    slug: competition.slug,
    externalId: competition.id,
    source: 'ephan',
    externalUrl: links.web || null,

    title: (competition.name || competition.shortName || '').trim() || 'Без назви',
    start,
    end,
    type: 'Змагання',
    town: location.city || null,
    venue: venueOf(location),
    organizator: competition.organizer?.name || null,

    // Рейтинговість видно з типу змагання; про спартаківські заходи джерело
    // не знає нічого, тому прапорець лишається за редактором.
    rating: type?.code === RANKING,
    spartak: false,
    foreign: !isUkrainian(competition),

    // Посилання на реєстрацію показуємо, лише поки вона відкрита: закрита веде
    // відвідувача в глухий кут.
    registration: registration.state === 'open' ? links.registration || links.web || null : null,
    link: links.web || null,
    resultsUrl: competition.resultsUrl || null,

    // Банер — окреме зображення на горизонтальний і вертикальний показ
    // (оновлений формат e-phan, 2026). Форму розбирає resolveBannerImage
    // у місці показу: горизонтальний банер — картки, вертикальний — сторінка
    // заходу. Легасі-поле media.banner (одна пряма адреса) джерело більше не
    // віддає.
    banner: banner?.horizontal || banner?.vertical ? banner : null,
    entry: media.entryForm ? { url: media.entryForm } : null,

    judges: null,
    hotels: null,
    address: null,
    organizations: [],
    partners: [],
  };
}

/** Список змагань у форму заходів. Неукраїнські лишаються — з позначкою `foreign`. */
export function eventsFromCompetitions(competitions = []) {
  return competitions.map(eventFromCompetition);
}
